import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  UncontrolledTooltip,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useSearchParams } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import { fetchFamilyDetailReportThunk } from "slices/Report/thunks";
import { exportToExcel } from "helpers/exportToExcel";
import { showSuccessToast, showErrorToast } from "helpers/toast_helper";
import { listFamilyDetailReport } from "services/reportService";

import FamilyGoalsContainer from "./familygoalsContainer";
import moment from "moment";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";

const selectReportState = (state: RootState) => (state as any).report;

const reportSelector = createSelector(selectReportState, (report) => ({
  familyDetailList: report.familyDetailList,
  loading: report.loading,
  pagination: report.pagination,
}));

const FamilyGoalsPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const [searchParams] = useSearchParams();
  const familyIdFromUrl = searchParams.get("familyid") ? Number(searchParams.get("familyid")) : 0;

  const { familyDetailList, loading, pagination } = useSelector(reportSelector);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10000);

  const rawFamilyDetailData = familyDetailList?.familydetails || [];

  // Transform nested data structure to flat rows for table
  // Each row represents a goal with contributor information
  // Uses exact API response keys - fully dynamic
  const transformedData = useMemo(() => {
    const rows: any[] = [];

    rawFamilyDetailData.forEach((familyMember: any) => {
      if (familyMember.goal_detail && familyMember.goal_detail.length > 0) {
        // Create a row for each goal
        familyMember.goal_detail.forEach((goal: any) => {
          // If there are contributors, create a row for each contributor
          if (goal.contributor && goal.contributor.length > 0) {
            goal.contributor.forEach((contributor: any) => {
              // Build contributor name from API response fields
              const contributorNameParts = [
                contributor.first_name,
                contributor.middle_name,
                contributor.last_name
              ].filter(Boolean);
              const contributorName = contributorNameParts.length > 0 
                ? contributorNameParts.join(" ").trim() 
                : null;

              rows.push({
                id: goal.goalid,
                user_id: goal.userid,
                goal_name: goal.goalname,
                goal_category: goal.goalcategory || goal.category_name,
                sub_category: goal.subcategory,
                target_amount: goal.targetamount,
                current_saving: goal.currentsaving,
                amount: contributor.amount,
                target_date: goal.targetdate,
                frequency_saving: goal.frequencysaving,
                goal_status: goal.goal_status,
                time_horizon: goal.timehorizon,
                contributor_name: contributorName,
                relationship: contributor.relationship_name,
                // Keep original data for reference
                originalData: familyMember,
                goalData: goal,
              });
            });
          } else {
            // If no contributors, create one row with goal data only
            rows.push({
              id: goal.goalid,
              user_id: goal.userid,
              goal_name: goal.goalname,
              goal_category: goal.goalcategory || goal.category_name,
              sub_category: goal.subcategory,
              target_amount: goal.targetamount,
              current_saving: goal.currentsaving,
              amount: null,
              target_date: goal.targetdate,
              frequency_saving: goal.frequencysaving,
              goal_status: goal.goal_status,
              time_horizon: goal.timehorizon,
              contributor_name: null,
              relationship: null,
              // Keep original data for reference
              originalData: familyMember,
              goalData: goal,
            });
          }
        });
      }
    });

    return rows;
  }, [rawFamilyDetailData]);

  // Apply search filter
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return transformedData;

    const query = searchQuery.toLowerCase();
    return transformedData.filter((row: any) => {
      return (
        String(row.id || "").toLowerCase().includes(query) ||
        String(row.user_id || "").toLowerCase().includes(query) ||
        String(row.goal_name || "").toLowerCase().includes(query) ||
        String(row.goal_category || "").toLowerCase().includes(query) ||
        String(row.sub_category || "").toLowerCase().includes(query) ||
        String(row.contributor_name || "").toLowerCase().includes(query) ||
        String(row.relationship || "").toLowerCase().includes(query) ||
        String(row.goal_status || "").toLowerCase().includes(query)
      );
    });
  }, [transformedData, searchQuery]);

  // Reset page when familyId changes
  useEffect(() => {
    setPage(1);
  }, [familyIdFromUrl]);

  useEffect(() => {
    if (familyIdFromUrl === 0) {
      return;
    }

    try {
      setError(null);
      dispatch(
        fetchFamilyDetailReportThunk({
          familyid: familyIdFromUrl,
          phone_no: "",
          email: "",
          page: page,
          page_size: limit,
        })
      ).catch((err: any) => {
        setError(err?.message || "Failed to fetch family details");
      });
    } catch (err: any) {
      setError(err?.message || "An error occurred");
    }
  }, [dispatch, page, limit, familyIdFromUrl]);

  const handlePageChange = (newPage: number) => {
    setError(null);
    setPage(newPage);
  };

  const handleRowClick = useCallback((row: any) => {
    // optional: highlight row or set selected id
  }, []);

  const handleSearch = () => {
    setPage(1);
    setError(null);
    try {
      dispatch(
        fetchFamilyDetailReportThunk({
          familyid: familyIdFromUrl,
          phone_no: "",
          email: "",
          page: 1,
          page_size: limit,
        })
      ).catch((err: any) => {
        setError(err?.message || "Search failed");
      });
    } catch (err: any) {
      setError(err?.message || "An error occurred");
    }
  };

  const formatDate = (d: string) => {
    if (!d) return "-";
    return moment(d).format("YYYY-MM-DD");
  };

  const handleDownload = useCallback(async () => {
    try {
      setError(null);
      
      if (!pagination?.total_pages) {
        showErrorToast("Pagination data missing!");
        return;
      }

      showSuccessToast("Preparing full export...");

      let allFamilyDetails: any[] = [];

      // Loop through all pages & fetch each page directly from API
      for (let p = 1; p <= pagination.total_pages; p++) {
        try {
          const response = await listFamilyDetailReport({
            familyid: familyIdFromUrl,
            phone_no: "",
            email: "",
            page: p,
            page_size: limit,
          });

          if (response?.success && response?.data?.familydetails) {
            allFamilyDetails = [...allFamilyDetails, ...response.data.familydetails];
          }
        } catch (pageError: any) {
          const errorMsg = pageError?.message || "Failed to fetch page " + p;
          showErrorToast(errorMsg);
          setError(errorMsg);
          return;
        }
      }

      if (allFamilyDetails.length === 0) {
        showErrorToast("No data available to export!");
        return;
      }

      // Transform the data for export using exact API response keys
      const exportData: any[] = [];
      allFamilyDetails.forEach((familyMember: any) => {
        if (familyMember.goal_detail && familyMember.goal_detail.length > 0) {
          familyMember.goal_detail.forEach((goal: any) => {
            if (goal.contributor && goal.contributor.length > 0) {
              goal.contributor.forEach((contributor: any) => {
                // Build contributor name from API response fields
                const contributorNameParts = [
                  contributor.first_name,
                  contributor.middle_name,
                  contributor.last_name
                ].filter(Boolean);
                const contributorName = contributorNameParts.length > 0 
                  ? contributorNameParts.join(" ").trim() 
                  : null;

                exportData.push({
                  "ID": goal.goalid,
                  "User ID": goal.userid,
                  "Goal Name": goal.goalname,
                  "Goal Category": goal.goalcategory || goal.category_name,
                  "Sub category": goal.subcategory,
                  "Target Amount": goal.targetamount,
                  "Current Saving": goal.currentsaving,
                  "Amount": contributor.amount,
                  "Target Date": goal.targetdate ? moment(goal.targetdate).format("YYYY-MM-DD") : null,
                  "Frequency Saving": goal.frequencysaving,
                  "Goal Status": goal.goal_status,
                  "Time Horizon": goal.timehorizon,
                  "Contributor name": contributorName,
                  "Relationship": contributor.relationship_name,
                });
              });
            } else {
              exportData.push({
                "ID": goal.goalid,
                "User ID": goal.userid,
                "Goal Name": goal.goalname,
                "Goal Category": goal.goalcategory || goal.category_name,
                "Sub category": goal.subcategory,
                "Target Amount": goal.targetamount,
                "Current Saving": goal.currentsaving,
                "Amount": null,
                "Target Date": goal.targetdate ? moment(goal.targetdate).format("YYYY-MM-DD") : null,
                "Frequency Saving": goal.frequencysaving,
                "Goal Status": goal.goal_status,
                "Time Horizon": goal.timehorizon,
                "Contributor name": null,
                "Relationship": null,
              });
            }
          });
        }
      });

      exportToExcel(exportData, "Family_Goals_Report");
      showSuccessToast("Family goals report exported successfully!");
    } catch (error: any) {
      const message =
        error?.message ||
        error?.response?.data?.message ||
        "Failed to export family details";
      setError(message);
      showErrorToast(message);
    }
  }, [pagination, limit, familyIdFromUrl]);

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "User ID",
        accessorKey: "user_id",
      },
      { 
        header: "Goal Name", 
        accessorKey: "goal_name",
      },
      { 
        header: "Goal Category", 
        accessorKey: "goal_category",
      },
      { 
        header: "Sub category", 
        accessorKey: "sub_category",
      },
      {
        header: "Target Amount",
        accessorKey: "target_amount",
      },
      {
        header: "Current Saving",
        accessorKey: "current_saving",
      },
      {
        header: "Amount",
        accessorKey: "amount",
      },
      {
        header: "Target Date",
        accessorKey: "target_date",
        cell: (cell: any) => formatDate(cell.getValue()),
      },
      { 
        header: "Frequency Saving", 
        accessorKey: "frequency_saving",
      },
      {
        header: "Goal Status",
        accessorKey: "goal_status",
        cell: (cell: any) => {
            const value = cell.getValue();
            const lowerVal = value?.toString().toLowerCase();
            const statusColor =
              lowerVal === "active" ? "#159E3A" : "#A65F00";
  
            return (
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: statusColor,
                  border: `1px solid ${statusColor}`,
                  textTransform: "capitalize",
                }}
              >
                {value}
              </span>
            );
          },
      },
      { 
        header: "Time Horizon", 
        accessorKey: "time_horizon",
      },
      { 
        header: "Contributor name", 
        accessorKey: "contributor_name",
      },
      { 
        header: "Relationship", 
        accessorKey: "relationship",
      },
    ],
    []
  );

  document.title = "Family Goals";

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Family Goals" pageTitle={["Report", "Family Goals"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search.."
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col md={7} className="text-end d-flex justify-content-end gap-2 align-items-center">
            {/* Download Button */}
            <Button
              id="download-btn-familygoals"
              color="primary"
              outline
              type="button"
              onClick={handleDownload}
              size="sm"
              style={{ minWidth: "40px", padding: "0.375rem 0.5rem" }}
            >
              <i className="ri-download-line" style={{ fontSize: "18px" }}></i>
            </Button>
            <UncontrolledTooltip 
              placement="top" 
              target="download-btn-familygoals"
            >
              <span style={{ color: 'white' }}>Download</span>
            </UncontrolledTooltip>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card id="familyGoalsList" className="border-0 shadow-none" style={{ border: "none" }}>
              <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                {/* Error Alert */}
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="ri-error-warning-line me-2"></i>
                    <strong>Error:</strong> {error}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setError(null)}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                <div className="tab-content">
                  {/* Loading State - Show immediately when loading */}
                  {loading && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                      <Loader />
                    </div>
                  )}

                  {/* No Family ID State */}
                  {!loading && !error && familyIdFromUrl === 0 && (
                    <div className="text-center py-5">
                      <div className="mb-3" style={{ fontSize: "3rem", color: "#ddd" }}>
                        <i className="ri-information-line"></i>
                      </div>
                      <h5 className="text-muted">No Family Selected</h5>
                      <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                        Please select a family member from the family detail page to view their goals.
                      </p>
                    </div>
                  )}

                  {/* Data State */}
                  {!loading && !error && familyIdFromUrl > 0 && filteredData?.length > 0 && (
                    <FamilyGoalsContainer
                      columns={columns}
                      data={filteredData}
                      pagination={undefined}
                      limit={limit}
                      setLimit={setLimit}
                      setPage={setPage}
                      onPageChange={(p: number) => handlePageChange(p)}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white border-top border-success"
                      trClass=""
                      divClass="table-responsive table-card"
                      loading={loading}
                      isError={!!error}
                      errorMessage={error || ""}
                    />
                  )}

                  {/* Empty State */}
                  {!loading && !error && familyIdFromUrl > 0 && (!filteredData || filteredData.length === 0) && (
                    <div className="text-center py-5">
                      <div className="mb-3" style={{ fontSize: "3rem", color: "#ddd" }}>
                        <i className="ri-inbox-line"></i>
                      </div>
                      <h5 className="text-muted">No records found</h5>
                      <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                        {searchQuery.trim()
                          ? "No goals match your search criteria."
                          : "There are no family goals to display for this family member."}
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FamilyGoalsPage;
